import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { FinanceClosureClientSample, FinanceClosureSummary, FinanceVendorSampleCost, FinanceNonSampleCost, FinanceClosureNote, ProjectDifotStatus } from '../models/finance';
import { ClosureVendorPricingEdit, VendorCpiSplit, VendorCpiSplitResult } from '../models/vendor-cpi-change';

@Injectable({
  providedIn: 'root'
})
export class FinanceService {
  baseUrl = environment.apiEndpoint + '/finance/';
  baseUrlZoho = environment.apiEndpoint + '/zoho/';

  tzoffset = new Date().getTimezoneOffset() * -1;

  constructor(private http: HttpClient) { }

  getSummaryVendorBreakdown(bidNumber): Observable<any[]> {
    return this.http.get<any[]>(this.baseUrl + `summary/${bidNumber}/vendorbreakdown`, { headers: { cache: '0' } });
  }

  getAllNonSampleCosts(bidNumber: string) {
    return this.http.get<FinanceNonSampleCost[]>(this.baseUrl + `summary/${bidNumber}/nonsamplecost`);
  }

  getAllVendorSampleCosts(bidNumber: string) {
    return this.http.get<FinanceVendorSampleCost[]>(this.baseUrl + `summary/${bidNumber}/vendorsamplecost`);
  }

  getNonSampleCostById(id: string) {
    return this.http.get<FinanceNonSampleCost>(this.baseUrl + `summary/nonsamplecost/${id}`);
  }

  getVendorSampleCostById(id: string) {
    return this.http.get<FinanceVendorSampleCost>(this.baseUrl + `summary/vendorsamplecost/${id}`);
  }

  addNonSampleCost(model: FinanceNonSampleCost) {
    return this.http.post(this.baseUrl + 'summary/nonsamplecost', model);
  }

  editNonSampleCost(model: FinanceNonSampleCost): Observable<any> {
    return this.http.put(this.baseUrl + `summary/nonsamplecost/${model.id}`, model);
  }

  addNonSampleCostBulk(model: FinanceNonSampleCost[]) {
    return this.http.post(this.baseUrl + 'summary/nonsamplecost/bulk', model);
  }

  deleteNonSampleCost(model: FinanceNonSampleCost) {
    return this.http.post(this.baseUrl + 'summary/nonsamplecost/delete', model);
  }

  deleteNonSampleCostBulk(model: FinanceNonSampleCost[]) {
    return this.http.post(this.baseUrl + 'summary/nonsamplecost/bulkdelete', model);
  }

  updateNonSampleCostBulk(model: FinanceNonSampleCost[]) {
    return this.http.post(this.baseUrl + 'summary/nonsamplecost/bulkupdate', model);
  }


  addVendorSampleCost(model: FinanceVendorSampleCost) {
    return this.http.post(this.baseUrl + 'summary/vendorsamplecost', model);
  }

  editVendorSampleCost(model: FinanceVendorSampleCost): Observable<any> {
    return this.http.put(this.baseUrl + `summary/vendorsamplecost/${model.id}`, model);
  }

  clientFileChecksFinanceClosure(s3File: string, bidNumber: any): Observable<any> {
    const params = new HttpParams().set('s3File', s3File);
    return this.http.post<any>(`${this.baseUrl}closure/ClientFileChecksFinanceClosure/${bidNumber}`, {}, { params });
  }

  saveFinanceClosure(model: FinanceClosureSummary) {
    return this.http.post(this.baseUrl + 'closure/saveFinanceClosure', model);
  }

  // Partial (PATCH-style) update — send a body with only the fields you want to
  // change. Only those keys are applied; an explicit null clears a field. The
  // record is located by the bidNumber in the path. Server recalc is not run.
  updateFinanceClosure(bidNumber: string, patch: Partial<FinanceClosureSummary>) {
    return this.http.post(this.baseUrl + `closure/updateFinanceClosure/${bidNumber}`, patch);
  }

  resetFinanceClosureStep(bidNumber: string, stepNumber: number) {
    return this.http.post(this.baseUrl + `closure/${bidNumber}/resetStep/${stepNumber}`, {});
  }

  invoiceFinanceClosure(model: any, closeStep: boolean = true) {
    return this.http.post(this.baseUrl + `closure/invoiceFinanceClosure?closeStep=${closeStep}`, model);
  }

  deleteFinanceClosureData(model: FinanceClosureSummary) {
    return this.http.post(this.baseUrl + 'closure/deleteFinanceClosureData/', model);
  }

  getFinanceClosureDataByBidNumber(bidNumber: string) {
    return this.http.get<FinanceClosureSummary>(this.baseUrl + `closure/getFinanceClosureDataByBidNumber/${bidNumber}`);
  }

  getIsSampleProject(bidNumber: string) {
    return this.http.get<boolean>(this.baseUrl + `summary/${bidNumber}/issample`);
  }

  // NK-194 - DIFOT verdict for one closure family.
  getProjectDifotStatus(bidNumber: string): Observable<ProjectDifotStatus> {
    return this.http.get<ProjectDifotStatus>(this.baseUrl + `closure/${bidNumber}/difot`, { headers: { cache: '0' } });
  }

  getAllFinanceClosureClientSample(financeClosureSummaryId: string) {
    return this.http.get<any>(this.baseUrl + `closure/${financeClosureSummaryId}/financeClosureClientSample`);
  }

  editFinanceClosureClientSample(model: FinanceClosureClientSample) {
    return this.http.put(this.baseUrl + `closure/financeClosureClientSample/${model.id}`, model);
  }

  getFinanceClosureVendors(financeClosureSummaryId: string) {
    return this.http.get<any>(this.baseUrl + `closure/${financeClosureSummaryId}/financeClosureVendors`);
  }

  editFinanceClosureVendors(model: any) {
    return this.http.put(this.baseUrl + `closure/financeClosureVendors/${model.id}`, model);
  }

  // NK-351. Splits one closure pricing row into two rates.
  //
  // Kept off editFinanceClosureVendors because it is not an edit to the vendor record: the
  // backend has to find the completes the row covers and restamp the ones that move, and it
  // answers with a reason when the row and its completes have drifted apart.
  splitClosureVendorSampleCpi(sampleId: string, split: VendorCpiSplit): Observable<VendorCpiSplitResult> {
    return this.http.post<VendorCpiSplitResult>(
      this.baseUrl + `closure/financeClosureVendorSample/${sampleId}/split`, split);
  }

  // NK-517. A closure vendor grid CPI or Total Cost edit. Unlike the split above it restamps
  // nothing, so the completes keep the rate they fielded at.
  saveClosureVendorPricing(financeClosureVendorsId: string, edit: ClosureVendorPricingEdit): Observable<VendorCpiSplitResult> {
    return this.http.post<VendorCpiSplitResult>(
      this.baseUrl + `closure/financeClosureVendors/${financeClosureVendorsId}/pricing`, edit);
  }

  // NK-351. The sell-side twin. A client pricing row is per segment rather than per vendor,
  // so it is a different row in a different table - the payload and the result are the same
  // shape, and the host picks the call by which grid it is.
  splitClosureClientSampleCpi(sampleId: string, split: VendorCpiSplit): Observable<VendorCpiSplitResult> {
    return this.http.post<VendorCpiSplitResult>(
      this.baseUrl + `closure/financeClosureClientSample/${sampleId}/split`, split);
  }

  checkProjectStatusforClosure(bidNumber: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + `closure/checkProjectStatusforClosure/${bidNumber}`);
  }

  updateProjectStatusToClosed(listOfIds: Array<string>): Observable<any> {
    return this.http.post(this.baseUrl + 'closure/updateProjectStatusToClosed', listOfIds);
  }

  downloadFinanceCompletes(bidNumber, filename) {
    return this.http.get(this.baseUrl + bidNumber + '/completes?filename=' + filename + '&tzoffset=' + this.tzoffset, { responseType: 'text' });
  }

  downloadClientFinanceCosts(bidNumber, filename) {
    return this.http.get(this.baseUrl + bidNumber + '/client-costs?filename=' + filename, { responseType: 'text' });
  }

  UpdateFinanceClosureSteps(stepNumber: number, bidNumber: string, isSampleProjects: boolean): Observable<any> {
    const params = new HttpParams()
      .set('stepNumber', stepNumber)
      .set('isSampleProjects', isSampleProjects);
    return this.http.post<any>(`${this.baseUrl}closure/UpdateFinanceClosureSteps/${bidNumber}`, {}, { params });
  }
  saveNote(model: FinanceClosureNote): Observable<any> {
    return this.http.post(this.baseUrl + 'closure/saveNote', model);
  }
  getAllProjectTeam(bidNumber: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + `closure/getAllProjectTeam/${bidNumber}`);
  }
  sendVendorExternalLink(closureVendor: any, bidNumber: string): Observable<any> {
    const payload = {
      bidNumber: bidNumber,
      vendor: closureVendor
    };
    return this.http.post<any>(this.baseUrl + 'closure/sendvendorexternallink', payload);
  }

  getZohoSalesOrder(bidNumber: string) {
    return this.http.get<any>(this.baseUrlZoho + `salesorder/${bidNumber}`);
  }

  getZohoProducts() {
    return this.http.get<any>(this.baseUrlZoho + `products`);
  }

  upsertZohoItems(bidNumber: string, items: any) {
    return this.http.post(this.baseUrlZoho + `salesorder/${bidNumber}/upsertitems`, items);
  }

  updateZohoSalesOrderItem(bidNumber: string, item: any, _delete: boolean = false): Observable<any> {
    return this.http.post(this.baseUrlZoho + `salesorder/${bidNumber}/updateitem?_delete=${_delete}`, item);
  }

  saveFinanceClosureResetAudit(financeClosureSummaryId: string, dto: any): Observable<any> {
    return this.http.post(this.baseUrl + `closure/${financeClosureSummaryId}/resetAudit`, dto);
  }

  getFinanceClosureResetAudits(financeClosureSummaryId: string): Observable<any[]> {
    return this.http.get<any[]>(this.baseUrl + `closure/${financeClosureSummaryId}/resetAudit`);
  }

  saveFinanceClosureResetAuditChanges(auditEntryId: string, changes: any[]): Observable<any> {
    return this.http.post(this.baseUrl + `closure/resetAudit/${auditEntryId}/changes`, changes);
  }

}

